<template>
  <div class="overlay" @click.self="close">
    <div class="dialog">
      <!-- Header -->
      <div class="dialog-header">
        <h2>🎰 룰렛 공유하기</h2>
        <button class="close-btn" @click="close"><i class="ph ph-x"></i></button>
      </div>

      <!-- Body -->
      <div class="dialog-body">
        <!-- 티켓 현황 -->
        <div class="ticket-info">
          <i class="ph-fill ph-ticket"></i>
          <span>보유 티켓: <strong>{{ remainingTickets }}장</strong></span>
        </div>

        <!-- Step 1: 받는 사람 -->
        <div class="section">
          <label class="section-label">받는 사람</label>
          <div class="input-tabs">
            <button :class="['tab', inputMode === 'direct' && 'active']" @click="inputMode = 'direct'">직접 입력</button>
            <button :class="['tab', inputMode === 'customer' && 'active']" @click="inputMode = 'customer'; searchCustomers()">고객 선택</button>
          </div>

          <!-- 직접 입력 -->
          <div v-if="inputMode === 'direct'" class="input-group">
            <input v-model="receiverName" placeholder="이름" class="input" />
            <input v-model="receiverPhone" placeholder="전화번호 (- 없이)" class="input" maxlength="11" @input="receiverPhone = receiverPhone.replace(/\D/g, '')" />
          </div>

          <!-- 고객 선택 -->
          <div v-if="inputMode === 'customer'" class="customer-select">
            <input v-model="customerSearch" placeholder="이름 또는 전화번호 검색" class="input" @input="searchCustomers" />
            <ul class="customer-list" v-if="customerResults.length > 0">
              <li 
                v-for="c in customerResults" 
                :key="c.idx" 
                :class="['customer-item', selectedCustomer?.idx === c.idx && 'selected']"
                @click="selectCustomer(c)"
              >
                <span class="cname">{{ c.name }}</span>
                <span class="cphone">{{ formatPhone(c.phone) }}</span>
              </li>
            </ul>
            <p v-else-if="customerSearch && !searchLoading" class="no-result">검색 결과 없음</p>
          </div>
        </div>

        <!-- Step 2: 뽑기 횟수 -->
        <div class="section">
          <label class="section-label">뽑기 횟수</label>
          <div class="count-selector">
            <button 
              v-for="n in 5" 
              :key="n" 
              :class="['count-btn', ticketCount === n && 'active']"
              :disabled="n > remainingTickets"
              @click="ticketCount = n"
            >
              {{ n }}회
            </button>
          </div>
          <p class="count-note">선택한 횟수만큼 티켓이 차감됩니다</p>
        </div>

        <!-- 경품 미리보기 -->
        <div class="section">
          <label class="section-label">경품 미리보기</label>
          <div class="prize-preview">
            <div class="prize-item" v-for="(prize, i) in prizes" :key="i">
              <span class="prize-icon">{{ prize.icon }}</span>
              <span class="prize-name">{{ prize.name }}</span>
            </div>
          </div>
        </div>

        <!-- 생성된 링크 -->
        <div v-if="generatedLink" class="section link-section">
          <label class="section-label">공유 링크</label>
          <div class="link-box">
            <input :value="generatedLink" readonly class="input link-input" ref="linkInput" />
            <button class="copy-btn" @click="copyLink">
              <i class="ph-fill ph-copy"></i>
            </button>
          </div>
          <div class="share-actions">
            <button class="action-btn kakao" @click="shareKakao">
              <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_small.png" /> 카카오톡
            </button>
            <button class="action-btn sms" @click="shareSMS">
              <i class="ph-fill ph-chat-circle-text"></i> 문자
            </button>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="dialog-footer">
        <button class="btn-cancel" @click="close">닫기</button>
        <button 
          v-if="!generatedLink"
          class="btn-create" 
          :disabled="!canCreate || creating"
          @click="createLink"
        >
          <i class="ph-fill ph-link" v-if="!creating"></i>
          <i class="ph ph-spinner" v-else style="animation: spin 1s linear infinite"></i>
          {{ creating ? '생성 중...' : '링크 생성' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, inject } from 'vue'

export default {
  props: {
    oth_close: { type: Function, required: true },
    remainingTickets: { type: Number, default: 0 },
    onShared: { type: Function }
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const member_info = inject('member_info')

    const inputMode = ref('direct')
    const receiverName = ref('')
    const receiverPhone = ref('')
    const customerSearch = ref('')
    const customerResults = ref([])
    const selectedCustomer = ref(null)
    const searchLoading = ref(false)
    const ticketCount = ref(1)
    const generatedLink = ref('')
    const creating = ref(false)
    const linkInput = ref(null)

    const prizes = [
      { icon: '☕', name: '커피 쿠폰' },
      { icon: '🍦', name: '아이스크림' },
      { icon: '🎫', name: '영화 티켓' },
      { icon: '💰', name: '현금 1만원' },
      { icon: '🍔', name: '치킨 쿠폰' },
    ]

    const canCreate = computed(() => {
      const hasReceiver = inputMode.value === 'direct'
        ? (receiverName.value.trim() && receiverPhone.value.length >= 10)
        : !!selectedCustomer.value
      return hasReceiver && ticketCount.value > 0 && ticketCount.value <= props.remainingTickets
    })

    async function searchCustomers() {
      if (!customerSearch.value || customerSearch.value.length < 1) {
        customerResults.value = []
        return
      }
      searchLoading.value = true
      try {
        const keyword = customerSearch.value.trim()
        const res = await sql_query(`
          SELECT idx, name, phone FROM customers 
          WHERE mb_id = '${member_info.mb_id}' 
            AND (name LIKE '%${keyword}%' OR phone LIKE '%${keyword}%')
          ORDER BY name ASC
          LIMIT 10
        `)
        customerResults.value = res || []
      } catch (e) {
        console.error(e)
      }
      searchLoading.value = false
    }

    function selectCustomer(c) {
      selectedCustomer.value = c
      receiverName.value = c.name
      receiverPhone.value = c.phone?.replace(/-/g, '') || ''
    }

    function generateToken() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
      let token = ''
      for (let i = 0; i < 24; i++) {
        token += chars.charAt(Math.floor(Math.random() * chars.length))
      }
      return token
    }

    async function createLink() {
      if (!canCreate.value || creating.value) return
      creating.value = true

      try {
        const token = generateToken()
        const name = receiverName.value.trim()
        const phone = receiverPhone.value.trim()
        const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 19).replace('T', ' ')

        // 1. roulette_links에 INSERT
        await sql_query(`
          INSERT INTO roulette_links (token, mb_id, receiver_phone, receiver_name, ticket_count, expires_at)
          VALUES ('${token}', '${member_info.mb_id}', '${phone}', '${name}', ${ticketCount.value}, '${expires}')
        `)

        // 2. 설계사 티켓 차감
        let remaining = ticketCount.value
        const tickets = await sql_query(`
          SELECT idx, ticket_count, used_count 
          FROM roulette_tickets 
          WHERE mb_id = '${member_info.mb_id}' 
            AND status = 'active' 
            AND (expired_at IS NULL OR expired_at > NOW())
            AND ticket_count > used_count
          ORDER BY expired_at ASC
        `)

        for (const t of (tickets || [])) {
          if (remaining <= 0) break
          const available = t.ticket_count - t.used_count
          const deduct = Math.min(available, remaining)
          await sql_query(`
            UPDATE roulette_tickets 
            SET used_count = used_count + ${deduct},
                status = CASE WHEN used_count + ${deduct} >= ticket_count THEN 'used' ELSE status END,
                updated_at = NOW()
            WHERE idx = ${t.idx}
          `)
          remaining -= deduct
        }

        // 3. 링크 생성
        const baseUrl = window.location.origin
        generatedLink.value = `${baseUrl}/roulette/${token}`

        // 4. 부모에 알림 (티켓 갱신)
        if (props.onShared) props.onShared()

      } catch (e) {
        console.error('링크 생성 실패:', e)
        alert('링크 생성에 실패했습니다.')
      }
      creating.value = false
    }

    function copyLink() {
      navigator.clipboard.writeText(generatedLink.value).then(() => {
        alert('링크가 복사되었습니다!')
      })
    }

    function shareKakao() {
      if (window.Kakao?.Share) {
        window.Kakao.Share.sendDefault({
          objectType: 'feed',
          content: {
            title: '🎰 행운의 룰렛에 초대합니다!',
            description: `${member_info.mb_name || '설계사'}님이 행운의 룰렛 ${ticketCount.value}회 뽑기를 선물했어요!`,
            imageUrl: `${window.location.origin}/imgs/common/roulette.png`,
            link: {
              mobileWebUrl: generatedLink.value,
              webUrl: generatedLink.value
            }
          },
          buttons: [{
            title: '룰렛 돌리기',
            link: {
              mobileWebUrl: generatedLink.value,
              webUrl: generatedLink.value
            }
          }]
        })
      } else {
        copyLink()
      }
    }

    function shareSMS() {
      const msg = `🎰 행운의 룰렛에 초대합니다!\n${ticketCount.value}회 뽑기 기회가 있어요!\n\n${generatedLink.value}`
      const phone = receiverPhone.value
      window.open(`sms:${phone}?body=${encodeURIComponent(msg)}`)
    }

    function formatPhone(phone) {
      if (!phone) return ''
      const p = phone.replace(/-/g, '')
      if (p.length === 11) return `${p.slice(0,3)}-${p.slice(3,7)}-${p.slice(7)}`
      if (p.length === 10) return `${p.slice(0,3)}-${p.slice(3,6)}-${p.slice(6)}`
      return phone
    }

    function close() {
      props.oth_close()
    }

    return {
      inputMode, receiverName, receiverPhone,
      customerSearch, customerResults, selectedCustomer, searchLoading,
      ticketCount, generatedLink, creating, linkInput,
      prizes, canCreate,
      searchCustomers, selectCustomer, createLink,
      copyLink, shareKakao, shareSMS, formatPhone, close
    }
  }
}
</script>

<style scoped>
@keyframes spin {
  to { transform: rotate(360deg) }
}

.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.dialog {
  background: var(--color-surface-deep, #fff);
  border-radius: 16px;
  width: 100%;
  max-width: 460px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border, #eee);
}

.dialog-header h2 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--color-text, #333);
  padding: 4px;
  border-radius: 6px;
}

.close-btn:hover {
  background: var(--color-background, #f5f5f5);
}

.dialog-body {
  padding: 20px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 티켓 현황 */
.ticket-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: 14px;
}

.ticket-info i {
  font-size: 20px;
}

.ticket-info strong {
  font-size: 18px;
}

/* 섹션 */
.section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-muted, #888);
}

/* 탭 */
.input-tabs {
  display: flex;
  gap: 6px;
}

.tab {
  flex: 1;
  padding: 8px;
  border: 1px solid var(--color-border, #ddd);
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--color-text, #333);
}

.tab.active {
  background: var(--color-brand, #5286FF);
  color: #fff;
  border-color: var(--color-brand, #5286FF);
}

/* 인풋 */
.input-group {
  display: flex;
  gap: 8px;
}

.input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid var(--color-border, #ddd);
  border-radius: 8px;
  font-size: 14px;
  background: var(--color-background, #f9f9f9);
  color: var(--color-text, #333);
  outline: none;
}

.input:focus {
  border-color: var(--color-brand, #5286FF);
}

/* 고객 리스트 */
.customer-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid var(--color-border, #ddd);
  border-radius: 8px;
}

.customer-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.15s;
  font-size: 14px;
}

.customer-item:hover {
  background: var(--color-background, #f5f5f5);
}

.customer-item.selected {
  background: var(--color-brand, #5286FF);
  color: #fff;
}

.cphone {
  opacity: 0.7;
  font-size: 13px;
}

.no-result {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-muted, #999);
  text-align: center;
  padding: 12px;
}

/* 횟수 선택 */
.count-selector {
  display: flex;
  gap: 8px;
}

.count-btn {
  flex: 1;
  padding: 10px;
  border: 2px solid var(--color-border, #ddd);
  border-radius: 10px;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--color-text, #333);
}

.count-btn.active {
  background: var(--color-brand, #5286FF);
  color: #fff;
  border-color: var(--color-brand, #5286FF);
}

.count-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.count-note {
  margin: 0;
  font-size: 12px;
  color: var(--color-text-muted, #999);
}

/* 경품 미리보기 */
.prize-preview {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.prize-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 20px;
  background: var(--color-background, #f5f5f5);
  font-size: 12px;
}

.prize-icon {
  font-size: 16px;
}

/* 링크 섹션 */
.link-section {
  padding: 16px;
  border-radius: 12px;
  background: var(--color-background, #f0f7ff);
  border: 1px dashed var(--color-brand, #5286FF);
}

.link-box {
  display: flex;
  gap: 6px;
}

.link-input {
  font-size: 12px;
  background: #fff;
}

.copy-btn {
  padding: 10px 14px;
  border: none;
  border-radius: 8px;
  background: var(--color-brand, #5286FF);
  color: #fff;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
}

.copy-btn:hover {
  opacity: 0.85;
}

.share-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.kakao {
  background: #FEE500;
  color: #3C1E1E;
}

.action-btn.kakao img {
  width: 18px;
  height: 18px;
}

.action-btn.sms {
  background: #34C759;
  color: #fff;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

/* 푸터 */
.dialog-footer {
  display: flex;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--color-border, #eee);
}

.btn-cancel, .btn-create {
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel {
  flex: 1;
  background: var(--color-background, #f5f5f5);
  color: var(--color-text, #333);
}

.btn-create {
  flex: 2;
  background: var(--color-brand, #5286FF);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-create:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-create:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(82, 134, 255, 0.4);
}
</style>